import { Smartphone, Headset, MonitorPlay, HelpCircle, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export type DeviceKind = 'mobile' | 'xr_headset' | 'control_ui' | 'unknown';

/**
 * Consistent icon + label per device type, used anywhere the UI needs to
 * tell a presenter which kind of client something applies to — the
 * device-count chips in RoomControl's header, and (as `variant="label"`)
 * plain inline captions like "Mobile only" on cards/sections that are
 * scoped to one device type. One place to update if a new client type
 * (e.g. Vision Pro as its own kind) ever needs distinguishing from
 * 'xr_headset'.
 */
const DEVICE_META: Record<DeviceKind, { icon: LucideIcon; label: string; plural: string }> = {
  mobile: { icon: Smartphone, label: 'Mobile', plural: 'phones' },
  xr_headset: { icon: Headset, label: 'Headset', plural: 'headsets' },
  control_ui: { icon: MonitorPlay, label: 'Control UI', plural: 'control UI' },
  unknown: { icon: HelpCircle, label: 'Unknown', plural: 'unknown' },
};

interface Props {
  type: DeviceKind;
  /** Shows "N phones"/"N headsets" instead of just the icon+label. Omit for a plain label-only badge. */
  count?: number;
  /** 'chip' (default): pill with icon + count, for the room header's live breakdown.
   *  'label': plain inline icon + text, for scoping a card/section to one device type. */
  variant?: 'chip' | 'label';
  className?: string;
}

export function DeviceBadge({ type, count, variant = 'chip', className }: Props) {
  const meta = DEVICE_META[type] ?? DEVICE_META.unknown;
  const Icon = meta.icon;

  if (variant === 'label') {
    return (
      <span className={cn('inline-flex items-center gap-1.5 text-muted-foreground', className)}>
        <Icon className="h-3.5 w-3.5" />
        {meta.label}
      </span>
    );
  }

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground',
        className,
      )}
      title={`${count ?? 0} ${meta.plural} connected`}
    >
      <Icon className="h-3.5 w-3.5" />
      {typeof count === 'number' ? count : null}
    </span>
  );
}
